import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import servePositionService from '../../services/servePositionService';
import { addNotification } from '../../store/slices/uiSlice';
import LoadingSpinner from '../common/LoadingSpinner';
import PositionFormModal from './PositionFormModal';
import useExcelBatch, { ExcelBatchButtons, ExcelImportConfirmModal } from '../../hooks/useExcelBatch';
import './PositionManagement.css';

const PositionManagement = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [categories, setCategories] = useState([]);

  const batch = useExcelBatch({
    exportUrl: '/api/v1/serve-positions/positions/export',
    templateUrl: '/api/v1/serve-positions/positions/import-template',
    importUrl: '/api/v1/serve-positions/positions/import',
    templateFilename: 'serve_position_import_template.xlsx',
    exportFilenamePrefix: 'serve_positions_export',
    upsertKey: 'position_name',
    getExistingKeys: async () => {
      const response = await servePositionService.getPositions({ limit: 1000 });
      return (response.items || []).map((p) => p.position_name).filter(Boolean);
    },
  });

  const categoryLabels = {
    'leadership': '領導層',
    'worship': '敬拜事工',
    'administration': '行政事務',
    'care': '關懷牧養',
    'outreach': '外展傳道',
    'general': '一般事奉'
  };

  useEffect(() => {
    loadCategories();
    loadPositions();
  }, [search, categoryFilter]);

  const loadCategories = async () => {
    try {
      const data = await servePositionService.getCategories();
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPositions = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 1000,
        search: search || undefined,
        category: categoryFilter || undefined
      };
      const response = await servePositionService.getPositions(params);
      setPositions(response.items || []);
    } catch (error) {
      console.error('Error loading positions:', error);
      dispatch(addNotification({
        type: 'error',
        message: '載入崗位失敗'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPosition(null);
    setShowFormModal(true);
  };

  const handleEdit = (position) => {
    setEditingPosition(position);
    setShowFormModal(true);
  };

  const handleDelete = async (positionId) => {
    if (!window.confirm('確定要刪除此崗位嗎？已分配的記錄也會一併刪除。')) return;

    try {
      await servePositionService.deletePosition(positionId);
      loadPositions();
      dispatch(addNotification({
        type: 'success',
        message: '刪除崗位成功'
      }));
    } catch (error) {
      console.error('Error deleting position:', error);
      dispatch(addNotification({
        type: 'error',
        message: '刪除崗位失敗'
      }));
    }
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    setEditingPosition(null);
    loadPositions();
    loadCategories();
    dispatch(addNotification({
      type: 'success',
      message: editingPosition ? '更新崗位成功' : '新增崗位成功'
    }));
  };

  const handleToggleActive = async (position) => {
    try {
      await servePositionService.updatePosition(position.id, {
        is_active: !position.is_active
      });
      loadPositions();
      dispatch(addNotification({
        type: 'success',
        message: position.is_active ? '已停用崗位' : '已啟用崗位'
      }));
    } catch (error) {
      console.error('Error toggling position status:', error);
      dispatch(addNotification({
        type: 'error',
        message: '更新崗位狀態失敗'
      }));
    }
  };

  if (loading && positions.length === 0) {
    return <LoadingSpinner text="載入中..." />;
  }

  // Group positions by category
  const groupedPositions = positions.reduce((acc, position) => {
    const category = position.category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(position);
    return acc;
  }, {});

  return (
    <div className="position-management">
      {/* Header Actions */}
      <div className="header-actions">
        <div className="filters">
          <input
            type="text"
            placeholder="搜尋崗位名稱..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">所有類別</option>
            {Object.entries(categoryLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExcelBatchButtons batch={batch} />
          <button onClick={handleCreate} className="btn-create">
            + 新增崗位
          </button>
        </div>
      </div>

      <ExcelImportConfirmModal batch={batch} onConfirm={loadPositions} />

      {/* Positions Grid */}
      <div className="positions-grid">
        {Object.entries(groupedPositions).map(([category, categoryPositions]) => (
          <div key={category} className="category-section">
            <h3 className="category-title">
              {categoryLabels[category] || category}
              <span className="count">({categoryPositions.length})</span>
            </h3>
            <div className="positions-list">
              {categoryPositions.map(position => (
                <div key={position.id} className={`position-card ${!position.is_active ? 'inactive' : ''}`}>
                  <div className="position-header">
                    <div className="position-title">
                      <h4>{position.position_name}</h4>
                      {position.position_name_en && (
                        <span className="position-en">{position.position_name_en}</span>
                      )}
                    </div>
                    <div className="position-count">
                      {position.assignment_count || 0} 人
                    </div>
                  </div>
                  
                  {position.description && (
                    <p className="position-description">{position.description}</p>
                  )}
                  
                  {position.requirements && (
                    <div className="position-requirements">
                      <strong>要求：</strong>
                      <span>{position.requirements}</span>
                    </div>
                  )}
                  
                  <div className="position-footer">
                    <div className="status-badge">
                      {position.is_active ? (
                        <span className="badge-active">啟用中</span>
                      ) : (
                        <span className="badge-inactive">已停用</span>
                      )}
                    </div>
                    <div className="position-actions">
                      <button
                        onClick={() => handleToggleActive(position)}
                        className="btn-toggle"
                        title={position.is_active ? '停用' : '啟用'}
                      >
                        {position.is_active ? '停用' : '啟用'}
                      </button>
                      <button
                        onClick={() => handleEdit(position)}
                        className="btn-edit"
                      >
                        編輯
                      </button>
                      <button
                        onClick={() => handleDelete(position.id)}
                        className="btn-delete"
                      >
                        刪除
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {positions.length === 0 && !loading && (
        <div className="empty-state">
          <p>沒有找到符合條件的崗位</p>
          <button onClick={handleCreate} className="btn-create">
            新增第一個崗位
          </button>
        </div>
      )}

      {/* Position Form Modal */}
      {showFormModal && (
        <PositionFormModal
          position={editingPosition}
          onClose={() => {
            setShowFormModal(false);
            setEditingPosition(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default PositionManagement;
